/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { httpsCallable } from 'firebase/functions'

import { functions } from '@/firebase'

// Constants
const DEFAULT_NUMBER_OF_CARDS = 5
const MAX_SHORT_ANSWER_LENGTH = 100
const TSV_SEPARATOR = '\t'

// Custom Error Classes
class ContentGenerationError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message)
    this.name = 'ContentGenerationError'
  }
}

class ValidationError extends Error {
  constructor(
    message: string,
    public readonly invalidItems: string[] = [],
  ) {
    super(message)
    this.name = 'ValidationError'
  }
}

// Type Definitions
type CardType = 'prompt' | 'question' | 'repeat' | 'short-answer'

interface GenerateStructuredOutputResponse {
  results: {
    content: string[]
  }
}

interface GenerateCardContentResponse {
  content: string
  error?: boolean
  message?: string
}

interface SchemaProperty {
  type: 'array' | 'string'
  description?: string
  items?: {
    type: string
    description?: string
  }
}

interface Schema {
  type: 'object'
  properties: {
    content: SchemaProperty
  }
  required: ['content']
}

// Content Processing Types
type ProcessableContent = string[] | string | Record<string, unknown>

// Firebase Function
const generateStructuredOutput = httpsCallable<{ prompt: string; schema: Schema }, GenerateStructuredOutputResponse>(
  functions,
  'generateStructuredOutput',
)

// Input Validation
const validateInputs = (
  prompt: string,
  language: string,
  translateTo: string,
  cardType: CardType,
  numberOfCards: number,
): void => {
  if (!prompt.trim()) {
    throw new ValidationError('Prompt cannot be empty')
  }
  if (!language.trim()) {
    throw new ValidationError('Language cannot be empty')
  }
  if (cardType === 'repeat' && !translateTo.trim()) {
    throw new ValidationError('Translation language is required for repeat cards')
  }
  if (numberOfCards <= 0 || numberOfCards > 50) {
    throw new ValidationError('Number of cards must be between 1 and 50')
  }
}

// Prompt Generation
const getPromptByCardType = (
  cardType: CardType,
  prompt: string,
  language: string,
  translateTo: string,
  numberOfCards: number,
): string => {
  const numCardsStr = numberOfCards.toString()
  const basePrompt = `Generate ${numCardsStr} language learning content items in ${language} based on the following prompt: "${prompt}"`

  const prompts = {
    prompt: `
      ${basePrompt}

      CARD TYPE: Conversation Prompts
      
      REQUIREMENTS:
      • Generate open-ended conversation starters or scenarios
      • All content must be in ${language}
      • Each item should encourage speaking and discussion
      • Keep content appropriate for language learners
      • Focus on practical, real-world situations
      • Make prompts engaging and thought-provoking
      
      EXAMPLES:
      • "Décris ta routine matinale idéale."
      • "Si tu pouvais voyager n'importe où, où irais-tu et pourquoi?"
      • "Parle-moi de ton plat préféré et explique pourquoi tu l'aimes."
      
      Generate exactly ${numCardsStr} conversation prompts.
    `,

    question: `
      ${basePrompt}

      CARD TYPE: Questions
      
      REQUIREMENTS:
      • Generate clear, focused questions with one correct answer
      • All content must be in ${language}
      • Questions should test comprehension, vocabulary, or grammar
      • Keep questions appropriate for language learners
      • Focus on factual or specific knowledge
      • Make questions clear and unambiguous
      
      EXAMPLES:
      • "Quelle est la capitale de la France?"
      • "Comment dit-on 'hello' en français?"
      • "Quel est le verbe principal dans la phrase 'Je mange une pomme'?"
      
      Generate exactly ${numCardsStr} short answer questions.
    `,

    repeat: `
      ${basePrompt}

      CARD TYPE: Vocabulary Repetition
      
      REQUIREMENTS:
      • Generate vocabulary items with translations
      • Original words/phrases must be in ${language}
      • Translations must be in ${translateTo}
      • Focus on practical, commonly used vocabulary
      • Keep content appropriate for language learners
      • Include a mix of nouns, verbs, adjectives, and phrases
      • Format each item as "original${TSV_SEPARATOR}translation" (tab-separated)
      
      EXAMPLES:
      • "la maison${TSV_SEPARATOR}the house"
      • "le chat${TSV_SEPARATOR}the cat"
      • "je suis${TSV_SEPARATOR}I am"
      
      Generate exactly ${numCardsStr} vocabulary items with translations in tab-separated format.
    `,

    'short-answer': `
      ${basePrompt}

      CARD TYPE: Short Answer Questions
      
      REQUIREMENTS:
      • Generate focused questions that elicit short, specific answers from students
      • All content must be in ${language}
      • Questions should be designed for answers of ${MAX_SHORT_ANSWER_LENGTH.toString()} characters or less
      • Keep questions appropriate for language learners
      • Make questions clear and unambiguous
      • Students will provide their own short answers to these questions
      
      EXAMPLES:
      • "Quelle est la capitale de la France?"
      • "Comment dit-on 'book' en français?"
      • "Quel est le pluriel de 'chat'?"
      • "Combien de jours y a-t-il dans une semaine?"
      
      Generate exactly ${numCardsStr} short answer questions.
    `,
  }

  return prompts[cardType] || basePrompt
}

// Schema Generation
const getSchemaByCardType = (cardType: CardType): Schema => {
  const baseSchema: Schema = {
    type: 'object',
    properties: {
      content: {
        type: 'array',
        items: { type: 'string' },
        description: 'Array of language learning content items',
      },
    },
    required: ['content'],
  }

  const descriptions = {
    prompt: 'A conversation prompt or scenario',
    question: 'A clear, focused question with one correct answer',
    repeat: `A vocabulary item in the format "word/phrase${TSV_SEPARATOR}translation" (tab-separated)`,
    'short-answer': 'A focused question designed to elicit a short answer from students',
  }

  return {
    ...baseSchema,
    properties: {
      content: {
        ...baseSchema.properties.content,
        items: {
          type: 'string',
          description: descriptions[cardType] || 'A language learning content item',
        },
        description: `Array of ${cardType} items for language learning`,
      },
    },
  }
}

// Content Processing Utilities
const processContentArray = (content: ProcessableContent): string[] => {
  if (Array.isArray(content)) {
    return content.filter((item): item is string => typeof item === 'string')
  }

  if (typeof content === 'string') {
    return content
      .split('\n')
      .map(item => item.trim())
      .filter(item => item !== '')
  }

  if (content && typeof content === 'object') {
    return Object.values(content)
      .flat()
      .filter((item): item is string => typeof item === 'string')
  }

  throw new ContentGenerationError(
    `Expected content to be an array, string, or object, but got ${typeof content}`,
    'INVALID_CONTENT_TYPE',
  )
}

// Validation Functions
const validateTSVFormat = (items: string[]): void => {
  const invalidItems = items.filter(item => {
    if (!item.includes(TSV_SEPARATOR)) return true

    const parts = item.split(TSV_SEPARATOR)

    return parts.length !== 2 || parts.some(part => !part.trim())
  })

  if (invalidItems.length > 0) {
    throw new ValidationError(
      'Invalid TSV format. Each item must contain exactly one tab separator with non-empty values on both sides.',
      invalidItems,
    )
  }
}

const validateGeneratedContent = (content: string[], cardType: CardType, requestedCount: number): void => {
  if (content.length === 0) {
    throw new ContentGenerationError('No valid content items generated', 'EMPTY_CONTENT')
  }

  if (cardType === 'repeat') {
    validateTSVFormat(content)
  }

  // Warn if we got fewer items than requested (but don't fail)
  if (content.length < requestedCount) {
    console.warn(
      `Generated ${content.length.toString()} items instead of requested ${requestedCount.toString()} for ${cardType} type`,
    )
  }
}

// Main Function
export const generateCardContent = async (
  prompt: string,
  language: string,
  translateTo: string,
  cardType: CardType,
  numberOfCards = DEFAULT_NUMBER_OF_CARDS,
): Promise<GenerateCardContentResponse> => {
  try {
    // Input validation
    validateInputs(prompt, language, translateTo, cardType, numberOfCards)

    // Generate prompt and schema
    const aiPrompt = getPromptByCardType(cardType, prompt, language, translateTo, numberOfCards)
    const schema = getSchemaByCardType(cardType)

    // Call the generation function
    const { data } = await generateStructuredOutput({
      prompt: aiPrompt,
      schema,
    })

    // Validate response structure
    if (!data?.results?.content) {
      throw new ContentGenerationError('No content returned from generateStructuredOutput', 'MISSING_CONTENT')
    }

    // Process and validate content
    const processedContentArray = processContentArray(data.results.content)

    validateGeneratedContent(processedContentArray, cardType, numberOfCards)

    // Take only the requested number of items and join
    const finalContent = processedContentArray.slice(0, numberOfCards).join('\n')

    return {
      content: finalContent,
      error: false,
    }
  } catch (error) {
    console.error('Error generating card content:', error)

    let message = 'Error generating card content'

    if (error instanceof ValidationError) {
      message = `Validation error: ${error.message}`
      if (error.invalidItems.length > 0) {
        message += ` Invalid items: ${error.invalidItems.join(', ')}`
      }
    } else if (error instanceof ContentGenerationError) {
      message = `Generation error: ${error.message}`
    } else if (error instanceof Error) {
      message = error.message
    }

    return {
      content: '',
      error: true,
      message,
    }
  }
}
